/**
 * Mapowanie zdarzeń interact / świata → FIRST_INTERACTION_* (PLAN-V2 §3.3).
 * Czysta logika — CompanionLayer.onWorldEvent konsumuje wynik.
 */
import type { CompanionIntentId } from '@pieskowo/shared';
import type { InteractEventType } from '../interact/types.js';

/** Intenty „pierwsza interakcja typu w sesji”. */
export type FirstInteractionIntent = Extract<
  CompanionIntentId,
  | 'FIRST_INTERACTION_TAP'
  | 'FIRST_INTERACTION_DRAG'
  | 'FIRST_INTERACTION_PICKUP'
  | 'FIRST_INTERACTION_GIVE'
  | 'FIRST_INTERACTION_CONSUME'
  | 'FIRST_INTERACTION_TOGGLE'
  | 'FIRST_INTERACTION_BOUNCE'
>;

const TYPE_TO_INTENT: Partial<Record<string, FirstInteractionIntent>> = {
  tap: 'FIRST_INTERACTION_TAP',
  'drag-start': 'FIRST_INTERACTION_DRAG',
  'drag-end': 'FIRST_INTERACTION_DRAG',
  drag: 'FIRST_INTERACTION_DRAG',
  pickup: 'FIRST_INTERACTION_PICKUP',
  give: 'FIRST_INTERACTION_GIVE',
  consume: 'FIRST_INTERACTION_CONSUME',
  toggle: 'FIRST_INTERACTION_TOGGLE',
  bounce: 'FIRST_INTERACTION_BOUNCE',
};

export type WorldEventLike = {
  type?: string;
  kind?: string;
  /** Zagnieżdżony interact z mission payload. */
  interact?: { type?: string; kind?: string };
};

/**
 * Zwraca intent FIRST_INTERACTION_* dla zdarzenia, albo null gdy nie dotyczy.
 */
export function firstInteractionIntentFromEvent(
  evt: WorldEventLike | null | undefined,
): FirstInteractionIntent | null {
  if (!evt) return null;
  const t = evt.type ?? evt.kind ?? evt.interact?.type ?? evt.interact?.kind;
  if (!t) return null;
  return TYPE_TO_INTENT[t] ?? null;
}

/** Czy typ zdarzenia to InteractEventType interesujący kotka. */
export function isCommentableInteractType(type: string): type is InteractEventType {
  return type in TYPE_TO_INTENT || type === 'drag-start' || type === 'drag-end';
}

/**
 * Sesyjny tracker: zwraca intent tylko przy PIERWSZYM wystąpieniu typu.
 */
export class FirstInteractionTracker {
  private seen = new Set<FirstInteractionIntent>();

  /** null gdy już komentowaliśmy ten typ w sesji. */
  observe(evt: WorldEventLike): FirstInteractionIntent | null {
    const intent = firstInteractionIntentFromEvent(evt);
    if (!intent) return null;
    if (this.seen.has(intent)) return null;
    this.seen.add(intent);
    return intent;
  }

  hasSeen(intent: FirstInteractionIntent): boolean {
    return this.seen.has(intent);
  }

  reset(): void {
    this.seen.clear();
  }

  get seenCount(): number {
    return this.seen.size;
  }
}
